/** Inline SVG icons (FOUNDATION) — no icon fonts, no CDNs (CSP: self only). */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function base(props: IconProps) {
  return {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    ...props,
  };
}

/** Cracked heart — the brand mark (Reject-Match). */
export function IconBrokenHeart(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 21s-8-5.2-8-11a4.6 4.6 0 0 1 8-3.1 4.6 4.6 0 0 1 8 3.1c0 5.8-8 11-8 11z" />
      <path d="M12.5 6.5 11 10l2.4 2.4-2 2.8 1.4 2.6" />
    </svg>
  );
}

/** Cracked heart, FILLED — the mascot mark (v3 §3): two halves split by a
 *  lightning crack. Solid signage weight for loading/empty/reveal moments. */
export function IconBrokenHeartFill(props: IconProps) {
  return (
    <svg {...base(props)} stroke="none" fill="currentColor">
      <path d="M12 6.4C10.8 3.9 4 4.1 4 9.9c0 4.5 4.7 8.2 7.9 11l.5-3.2-1.2-2.6 1.9-2.8-2.2-2.7L12 6.4z" />
      <path d="M12.9 6.6c1.2-2.6 7.1-2.4 7.1 3.3 0 4.5-4.5 8.1-7.6 10.7l.9-2.7-1.2-2.6 1.9-2.8-2.2-2.6 1.1-3.3z" />
    </svg>
  );
}

/** Card stack — the Deck tab. */
export function IconDeck(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="7" y="4" width="12" height="16" rx="2.5" />
      <path d="M5 6.5v12A2.5 2.5 0 0 0 7.5 21" opacity="0.6" />
      <path d="M11.2 12.9c-.8-.8-1.2-1.6-1.2-2.4 0-1.9 2.2-2.5 3-1a1.66 1.66 0 0 1 3 1c0 .8-.4 1.6-1.2 2.4L13 14.7l-1.8-1.8z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Fans & Haters — heart + broken heart, equal weight. */
export function IconLikes(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M7 13.5s-4.5-3-4.5-6.2A2.7 2.7 0 0 1 7 5.5a2.7 2.7 0 0 1 4.5 1.8C11.5 10.5 7 13.5 7 13.5z" />
      <path d="M17 21s-4.5-3-4.5-6.2A2.7 2.7 0 0 1 17 13a2.7 2.7 0 0 1 4.5 1.8C21.5 18 17 21 17 21z" />
      <path d="M17.6 13.6l-.8 1.8 1.3 1.2-1 1.5.7 1.4" strokeWidth="1.3" />
    </svg>
  );
}

/** Chat bubble — Matches tab. */
export function IconChat(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.6L4 20l1-4.4A8.5 8.5 0 1 1 21 11.5z" />
      <path d="M8.5 10.5h7M8.5 13.5h4.5" />
    </svg>
  );
}

/** Store bag. */
export function IconStore(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 8h14l-1 12a2 2 0 0 1-2 1.8H8A2 2 0 0 1 6 20L5 8z" />
      <path d="M9 10V6a3 3 0 0 1 6 0v4" />
    </svg>
  );
}

/** Profile person. */
export function IconProfile(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5 20.2c.8-3.6 3.6-5.4 7-5.4s6.2 1.8 7 5.4" />
    </svg>
  );
}

/** Skull — Super Reject. */
export function IconSkull(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 3a7.5 7.5 0 0 0-7.5 7.5c0 2.6 1.3 4.7 3.2 6v3a1.5 1.5 0 0 0 1.5 1.5h5.6a1.5 1.5 0 0 0 1.5-1.5v-3c1.9-1.3 3.2-3.4 3.2-6A7.5 7.5 0 0 0 12 3z" />
      <circle cx="9" cy="11" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="15" cy="11" r="1.4" fill="currentColor" stroke="none" />
      <path d="M11 15.5h2" />
    </svg>
  );
}

/** Shield. */
export function IconShield(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 3l7 2.8v5.4c0 4.6-3 8-7 9.8-4-1.8-7-5.2-7-9.8V5.8L12 3z" />
      <path d="M9.2 12l2 2 3.6-3.8" />
    </svg>
  );
}

/** Download / install arrow. */
export function IconInstall(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 4v10m0 0l-4-4m4 4l4-4" />
      <path d="M5 19h14" />
    </svg>
  );
}
